import { NgClass } from '@angular/common'
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  Injector,
  OnInit,
  Signal,
  computed,
  effect,
  inject,
  input,
  runInInjectionContext,
} from '@angular/core'

import { FlxStyleModule, FlxThemeModule, FlxWrapperStyleDirective } from '@flowx/angular-theme'
import { RatingTemplateConfig } from '@flowx/core-sdk'

import { getBoolExpressionSignal, getDynamicPropSignal } from '../helpers'

@Component({
  selector: 'flx-rating-wrapper',
  hostDirectives: [
    { directive: FlxWrapperStyleDirective, inputs: ['flxWrapperStyle: templateConfig'] },
  ],
  imports: [NgClass, FlxThemeModule, FlxStyleModule],
  templateUrl: './rating-wrapper.component.html',
  styleUrl: './rating-wrapper.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FlxRatingWrapperComponent implements OnInit, AfterViewInit {
  readonly #injector = inject(Injector)
  readonly #flxWrapperStyle = inject(FlxWrapperStyleDirective)

  templateConfig = input.required<RatingTemplateConfig>()

  displayOptions = computed(() => this.templateConfig().displayOptions)
  flowxProps = computed(() => this.displayOptions().flowxProps)

  // expressions
  isHidden: Signal<boolean>
  // dynamic props
  value: Signal<string>

  readonly max = computed(() => this.flowxProps()?.max ?? 5)

  readonly stars = computed(() =>
    Array.from({ length: this.max() }, (_, index) => index < (Number(this.value()) || 0))
  )

  readonly sizeClass = computed(() => `rating--${this.flowxProps()?.size || 'md'}`)
  readonly hostClass = computed(() =>
    [this.displayOptions().className, this.sizeClass()].filter(Boolean).join(' ')
  )

  ngOnInit(): void {
    runInInjectionContext(this.#injector, () => {
      const props = this.flowxProps()
      const templateConfig = this.templateConfig()
      this.isHidden = getBoolExpressionSignal(props.hide, templateConfig)
      this.value = getDynamicPropSignal(String(props.value ?? ''), templateConfig)
    })
  }

  ngAfterViewInit(): void {
    runInInjectionContext(this.#injector, () => {
      effect(() => {
        this.#flxWrapperStyle.toggleDisplay(this.isHidden())
      })
    })
  }
}
